<template>
  <component :is="tag" :class="b(modifiers)">
    <span :class="b('name', { invisible, required })">
      <slot v-if="$slots.name" name="name"></slot>
      <template v-else>
        {{ name }}
      </template>
    </span>
    <component v-if="$slots.default" :is="innerTag" :class="b('inner')">
      <!-- @slot Label content -->
      <slot @focus="focus = true" @blur="focus = false"></slot>
    </component>
  </component>
</template>

<script lang="ts">
  import { defineComponent, toRefs } from 'vue';
  import useFormStates, { FormStates, withProps } from '@/compositions/form-states';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';

  /**
   * Label component for form elements, can be used with a slot or a for attribute
   */
  export default defineComponent({
    name: 'e-label',

    // components: {},

    props: {
      ...withProps(),

      /**
       * Displayed tag.
       */
      tag: {
        type: String,
        default: 'label',
      },

      /**
       * Allows to set the type of the inner tag.
       */
      innerTag: {
        type: String,
        default: 'span',
      },

      /**
       * Displayed name of the label
       */
      name: {
        type: String,
        default: '',
      },

      /**
       * Position of the label ("top", "right", "bottom", "left")
       */
      position: {
        type: String,
        default: 'top',
        validator: (value: string) => [
          'top',
          'right',
          'bottom',
          'left',
        ].includes(value),
      },

      /**
       * Hides the label-text for the normal user (only available for screen readers).
       */
      invisible: {
        type: Boolean,
        default: false,
      },

      /**
       * Adds an optional required marker "*".
       */
      required: {
        type: Boolean,
        default: false,
      },
    },

    setup(props): FormStates {
      return {
        ...useFormStates(toRefs(props).state),
      };
    },

    computed: {
      /**
       * Defines state modifier classes.
       */
      modifiers(): Modifiers {
        return {
          ...this.stateModifiers,
          position: this.position,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},
    // methods: {}
    // render() {},
  });
</script>

<style lang="scss">
  @use '../setup/scss/mixins';
  @use '../setup/scss/variables';

  .e-label {
    $this: &;

    display: block;
    cursor: pointer;
    color: variables.$color-grayscale--0;

    &__inner {
      width: 100%;
      cursor: initial;
    }

    &__name {
      @include mixins.font(variables.$font-size--16, variables.$spacing--20, variables.$font-weight--bold);

      display: block;
      color: inherit;

      &--required::after {
        content: '*';
        color: variables.$color-primary--1;
      }
    }

    &__name--invisible {
      @include mixins.invisible();
    }

    &--position-top {
      #{$this}__name {
        margin-bottom: variables.$spacing--5;
      }
    }

    &--position-bottom {
      display: flex;
      flex-direction: column-reverse;

      #{$this}__name {
        margin-top: variables.$spacing--5;
      }

      #{$this}__inner {
        flex-shrink: 0; // fixes height bug on IE11
      }
    }

    &--position-left {
      display: flex;

      #{$this}__name {
        display: flex;
        flex: 1 0 auto;
        align-items: center;
        margin-right: variables.$spacing--15;
      }
    }

    &--position-right {
      display: flex;
      flex-direction: row-reverse;

      #{$this}__name {
        display: flex;
        flex: 1 0 auto;
        align-items: center;
        margin-left: variables.$spacing--15;
      }
    }

    &--active,
    &--focus {
      color: variables.$color-primary--2;
    }

    &--disabled {
      color: variables.$color-grayscale--400;
    }

    &--state-error {
      color: variables.$color-status--error;
    }
  }
</style>
